/**
 * HTTP(S) URLs are signed playback URLs that the browser can stream directly.
 */
export function isHttpAudioUrl(audioUrl: string | null | undefined): boolean {
    return !!audioUrl && /^https?:\/\//i.test(audioUrl);
}

/**
 * Locally created preview and fallback URLs use the `blob:` scheme. Treat
 * them as already-resolved playback sources so we do not repeatedly re-enter
 * the remote URL resolution path.
 */
export function isBlobAudioUrl(audioUrl: string | null | undefined): boolean {
    return !!audioUrl && /^blob:/i.test(audioUrl);
}

const RECORDABLE_AUDIO_MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
];

/**
 * Pick a recording MIME type that the browser can both record and play back.
 * Unsaved previews use the raw MediaRecorder blob, so choosing a type that is
 * only recordable is not good enough.
 */
export function getPreferredAudioRecordingMimeType(data: {
    canPlayType: (mimeType: string) => string,
    isMediaRecorderTypeSupported: ((mimeType: string) => boolean) | null,
}): string | null {
    const {
        canPlayType,
        isMediaRecorderTypeSupported,
    } = data;

    if (!isMediaRecorderTypeSupported) {
        return null;
    }

    return RECORDABLE_AUDIO_MIME_CANDIDATES.find((mimeType) => {
        if (!isMediaRecorderTypeSupported(mimeType)) {
            return false;
        }

        const baseMimeType = mimeType.split(';')[0];
        return (
            canPlayType(mimeType) !== ''
            || canPlayType(baseMimeType) !== ''
        );
    }) ?? null;
}

/**
 * Some browsers record using codec-qualified MIME strings that they later
 * refuse to play back from a blob URL. For local preview we normalize those
 * strings down to the base container MIME type.
 */
export function normalizeRecordedAudioBlobMimeType(
    mimeType: string | null | undefined,
): string {
    const normalizedMimeType = (mimeType || '').trim().toLowerCase();
    if (!normalizedMimeType) {
        return '';
    }

    return normalizedMimeType.split(';')[0];
}

/**
 * Firebase media URLs can hide the real file extension inside an encoded
 * storage path. Use that extension when the fetched blob does not carry a
 * usable MIME type of its own.
 */
export function inferAudioMimeTypeFromUrl(
    audioUrl: string | null | undefined,
): string {
    if (!audioUrl) {
        return '';
    }

    let normalizedPath = '';

    try {
        const parsedUrl = new URL(audioUrl);
        normalizedPath = decodeURIComponent(`${parsedUrl.pathname}${parsedUrl.search}`);
    } catch {
        normalizedPath = decodeURIComponent(audioUrl);
    }

    if (/\.mp3(\b|$)/i.test(normalizedPath)) {
        return 'audio/mpeg';
    }
    if (/\.wav(\b|$)/i.test(normalizedPath)) {
        return 'audio/wav';
    }
    if (/\.webm(\b|$)/i.test(normalizedPath)) {
        return 'audio/webm';
    }
    if (/\.(m4a|mp4)(\b|$)/i.test(normalizedPath)) {
        return 'audio/mp4';
    }
    if (/\.(ogg|opus)(\b|$)/i.test(normalizedPath)) {
        return 'audio/ogg';
    }
    if (/\.flac(\b|$)/i.test(normalizedPath)) {
        return 'audio/flac';
    }

    return '';
}

/**
 * Audio notes can be rewritten from a non-mp3 source to a canonical mp3
 * playback object. Try the canonical mp3 path first, then fall back to the
 * originally stored object path.
 */
export function getAudioPlaybackCandidatePaths(playableFilePath: string | null): string[] {
    if (!playableFilePath) {
        return [];
    }

    const mp3Path = playableFilePath.replace(/\.[^/.]+$/, '.mp3');
    if (mp3Path === playableFilePath) {
        return [playableFilePath];
    }

    return [mp3Path, playableFilePath];
}

/**
 * Post audio is now treated as protected media. Reader playback should come
 * from a backend-issued URL instead of direct Storage SDK resolution so the
 * Storage rules can stay narrow without breaking the player.
 */
export function isProtectedPostAudioStoragePath(
    storagePath: string | null | undefined,
): boolean {
    if (!storagePath) {
        return false;
    }

    return /^(staging-)?posts\/audio\//.test(storagePath);
}

/**
 * Build the public Firebase Storage media URL from a storage object path and
 * the object's download token metadata.
 */
export function buildFirebaseStorageMediaUrl(data: {
    storageBucket: string,
    storagePath: string,
    downloadToken: string,
}): string {
    const {
        storageBucket,
        storagePath,
        downloadToken,
    } = data;

    return `https://firebasestorage.googleapis.com/v0/b/${
        encodeURIComponent(storageBucket)
    }/o/${
        encodeURIComponent(storagePath)
    }?alt=media&token=${
        encodeURIComponent(downloadToken)
    }`;
}

interface IStorageMetadataResponse {
    downloadTokens?: string | null,
}

/**
 * Resolve a remote audio note playback URL. We first ask the Firebase Storage
 * SDK for a signed URL. If that fails in the browser, we fall back to the
 * object's download token metadata and construct the public media URL
 * ourselves.
 */
export async function resolveAudioPlaybackUrl(data: {
    audioUrl: string | null,
    playableFilePath: string | null,
    storageBucket: string | null | undefined,
    getSignedUrl: (path: string) => Promise<string>,
    getStorageMetadata: (path: string) => Promise<IStorageMetadataResponse | null>,
}): Promise<string | null> {
    const {
        audioUrl,
        playableFilePath,
        storageBucket,
        getSignedUrl,
        getStorageMetadata,
    } = data;

    if (isHttpAudioUrl(audioUrl)) {
        return audioUrl;
    }

    if (!playableFilePath) {
        return null;
    }

    if (isProtectedPostAudioStoragePath(playableFilePath)) {
        return null;
    }

    const candidatePaths = getAudioPlaybackCandidatePaths(playableFilePath);
    const resolveCandidatePath = async (candidatePath: string): Promise<string | null> => {
        try {
            return await getSignedUrl(candidatePath);
        } catch {
            // Fall through to the metadata-token path for this candidate.
        }

        try {
            const metadata = await getStorageMetadata(candidatePath);
            const downloadToken = metadata?.downloadTokens?.split(',').find(Boolean)?.trim();

            if (downloadToken && storageBucket) {
                return buildFirebaseStorageMediaUrl({
                    storageBucket,
                    storagePath: candidatePath,
                    downloadToken,
                });
            }
        } catch {
            // Continue to the next candidate path.
        }

        return null;
    };

    const resolveCandidatePaths = async (paths: string[]): Promise<string | null> => {
        const [nextPath, ...remainingPaths] = paths;

        if (!nextPath) {
            return null;
        }

        const resolvedUrl = await resolveCandidatePath(nextPath);
        if (resolvedUrl) {
            return resolvedUrl;
        }

        return resolveCandidatePaths(remainingPaths);
    };

    return resolveCandidatePaths(candidatePaths);
}

/**
 * Returns the persisted storage path that should back playback for an audio
 * note. Temporary auto-record identifiers are editor placeholders and should
 * never be treated as real media paths.
 */
export function getPlayableAudioStoragePath(data: {
    filePath: string | null,
    remoteFilePath: string | null,
    autoRecordIdentifier: string | null,
}): string | null {
    const {
        filePath,
        remoteFilePath,
        autoRecordIdentifier,
    } = data;

    const candidatePath = remoteFilePath || filePath;
    if (!candidatePath) return null;
    if (candidatePath === autoRecordIdentifier) return null;
    return candidatePath;
}

/**
 * Saved audio notes should switch to remote playback whenever there is no
 * local preview blob left to play. This applies in both write mode and read
 * mode.
 */
export function shouldResolveRemotePlaybackUrl(data: {
    audioBlob: Blob | null,
    audioUrl: string | null,
    playableFilePath: string | null,
}): boolean {
    const {
        audioBlob,
        audioUrl,
        playableFilePath,
    } = data;

    if (audioBlob != null) return false;
    if (!playableFilePath) return false;
    if (isHttpAudioUrl(audioUrl)) return false;
    if (isBlobAudioUrl(audioUrl)) return false;
    return true;
}
